import { Component, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { Button } from './ui/Button';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onReset?: () => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('[ErrorBoundary] Caught error:', error);
    console.error('[ErrorBoundary] Error info:', errorInfo);
    this.setState({ errorInfo });
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    this.props.onReset?.();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen bg-[#0f172a] flex flex-col items-center justify-center p-6">
          <div className="w-full max-w-lg text-center">
            {/* Error Icon */}
            <div className="mb-8 flex justify-center">
              <div className="w-24 h-24 bg-red-500/20 rounded-full flex items-center justify-center">
                <AlertTriangle className="size-12 text-red-400" />
              </div>
            </div>

            {/* Error Message */}
            <h1 className="text-3xl text-white mb-4">Something Went Wrong</h1>
            <p className="text-lg text-slate-400 mb-8">
              We apologize for the inconvenience. An unexpected error has occurred.
            </p>

            {/* Error Details (Development only) */}
            {import.meta.env.DEV && this.state.error && (
              <div className="mb-8 p-4 bg-slate-800 rounded-xl text-left overflow-auto max-h-48">
                <p className="text-red-400 font-mono text-sm mb-2">
                  {this.state.error.message}
                </p>
                {this.state.errorInfo && (
                  <pre className="text-slate-500 text-xs whitespace-pre-wrap">
                    {this.state.errorInfo.componentStack}
                  </pre>
                )}
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-col gap-4">
              <Button
                onClick={this.handleRetry}
                className="w-full h-16 text-xl bg-emerald-600 hover:bg-emerald-700 text-white rounded-2xl shadow-lg transition-all"
              >
                <RefreshCw className="size-6 mr-3" />
                Try Again
              </Button>
              <Button
                onClick={this.handleGoHome}
                variant="outline"
                className="w-full h-14 text-lg bg-slate-800 border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white rounded-xl"
              >
                <Home className="size-5 mr-3" />
                Return to Start
              </Button>
            </div>

            {/* Support Info */}
            <p className="text-slate-500 mt-8 text-sm">
              If this problem persists, please contact support.
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
